import React from 'react';
import { ChevronDown, CheckCircle2, Clock, Circle } from 'lucide-react';

const ProcessFlowDiagram = ({ processSequence, processStatuses, onProcessClick, className = '' }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Complete':
        return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case 'In Progress':
        return <Clock className="w-5 h-5 text-orange-400" />;
      case 'Ready':
        return <Circle className="w-5 h-5 text-blue-400 fill-blue-400" />;
      default:
        return <Circle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Complete':
        return 'border-green-400 bg-green-950/30';
      case 'In Progress':
        return 'border-orange-400 bg-orange-950/30';
      case 'Ready':
        return 'border-blue-400 bg-blue-950/30';
      default:
        return 'border-border bg-muted/20';
    }
  };

  // Helper to get status - supports both index-based (new) and name-based (legacy) formats
  const getStatus = (index, processName) => {
    // Try index-based first (new format)
    if (processStatuses[String(index)] !== undefined) {
      return processStatuses[String(index)];
    }
    // Fall back to name-based (legacy format)
    return processStatuses[processName] || 'Not Ready';
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`} data-testid="process-flow-diagram">
      {processSequence.map((process, index) => {
        const status = getStatus(index, process);
        const isClickable = onProcessClick !== undefined;
        
        return (
          <React.Fragment key={index}>
            <button
              onClick={() => isClickable && onProcessClick(process, index)}
              disabled={!isClickable}
              className={`
                relative p-4 border-2 rounded-sm transition-all
                ${getStatusColor(status)}
                ${isClickable ? 'hover:border-primary cursor-pointer hover:scale-[1.02]' : 'cursor-default'}
                group
              `}
              data-testid={`process-step-${index}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted/50 font-mono text-sm font-bold text-white">
                    {index + 1}
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-white uppercase tracking-wide text-sm">
                      {process}
                    </p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      {status}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(status)}
                  {isClickable && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-primary text-xs font-bold">
                      CLICK TO EDIT
                    </div>
                  )}
                </div>
              </div>

              {/* Progress line connector */}
              {status === 'Complete' && index < processSequence.length - 1 && (
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0.5 h-2 bg-green-400" />
              )}
            </button>

            {/* Arrow between steps */}
            {index < processSequence.length - 1 && (
              <div className="flex justify-center">
                <ChevronDown className={`w-6 h-6 ${
                  status === 'Complete' ? 'text-green-400' : 'text-muted-foreground'
                }`} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ProcessFlowDiagram;
